if (!customElements.get('cart-upsell-carousel')) {
  customElements.define(
    'cart-upsell-carousel',
    class CartUpsellCarousel extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.track = this.querySelector('.cart-upsell__track');
        this.previousButton = this.querySelector('[data-upsell-previous]');
        this.nextButton = this.querySelector('[data-upsell-next]');
        this.controls = this.querySelector('.cart-upsell__controls');
        this.ownedQuickAddModals = Array.from(this.querySelectorAll('quick-add-modal[data-cart-upsell-modal]'));
        // A fixed-position modal is constrained by the cart drawer because the
        // drawer uses transform for its slide animation. Portal cart upsell
        // modals to the document body so they use the full viewport, matching
        // every other quick-add modal in the theme.
        this.ownedQuickAddModals.forEach((modal) => document.body.appendChild(modal));
        this.isRtl = getComputedStyle(this).direction === 'rtl';
        this.dragDistance = 0;

        if (!this.track || !this.previousButton || !this.nextButton) return;

        this.previousButton.addEventListener('click', () => this.move(-1));
        this.nextButton.addEventListener('click', () => this.move(1));
        this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
        this.track.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
        this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.track.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.track.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.track.addEventListener('pointercancel', (event) => this.onPointerUp(event));
        this.track.addEventListener('click', (event) => this.preventDraggedLink(event), true);

        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this.track);
        requestAnimationFrame(() => this.updateControls());
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.ownedQuickAddModals?.forEach((modal) => modal.remove());
      }

      get cards() {
        return Array.from(this.track.querySelectorAll('.cart-upsell__item'));
      }

      get isScrollable() {
        return this.track.scrollWidth > this.track.clientWidth + 2;
      }

      isVisible(card) {
        const trackRect = this.track.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        return cardRect.left >= trackRect.left - 2 && cardRect.right <= trackRect.right + 2;
      }

      updateControls() {
        const cards = this.cards;
        const scrollable = this.isScrollable && cards.length > 1;
        this.controls.hidden = !scrollable;
        if (!scrollable) return;

        this.previousButton.disabled = this.isVisible(cards[0]);
        this.nextButton.disabled = this.isVisible(cards[cards.length - 1]);
      }

      queueUpdate() {
        cancelAnimationFrame(this.updateFrame);
        this.updateFrame = requestAnimationFrame(() => this.updateControls());
      }

      move(direction) {
        const firstCard = this.cards[0];
        if (!firstCard) return;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const distance = firstCard.getBoundingClientRect().width + gap;
        const left = direction * distance * (this.isRtl ? -1 : 1);
        this.track.scrollBy({ left, behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
      }

      onWheel(event) {
        if (!this.isScrollable || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
        const movingToEnd = event.deltaY > 0;
        if ((movingToEnd && this.nextButton.disabled) || (!movingToEnd && this.previousButton.disabled)) return;

        event.preventDefault();
        const left = event.deltaY * (this.isRtl ? -1 : 1);
        this.track.scrollBy({ left, behavior: 'auto' });
      }

      onPointerDown(event) {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        // Keep a normal press as a real click. Capturing the pointer here makes
        // the carousel track become the click target, so links, modal openers,
        // and add-to-cart buttons inside a card never receive their click.
        this.pointerDown = true;
        this.dragging = false;
        this.activePointerId = event.pointerId;
        this.dragDistance = 0;
        this.dragStartX = event.clientX;
        this.dragStartScroll = this.track.scrollLeft;
      }

      onPointerMove(event) {
        if (!this.pointerDown || event.pointerId !== this.activePointerId) return;
        const delta = event.clientX - this.dragStartX;
        this.dragDistance = Math.max(this.dragDistance, Math.abs(delta));

        if (!this.dragging && this.dragDistance > 5) {
          this.dragging = true;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture(event.pointerId);
        }

        if (!this.dragging) return;
        this.track.scrollLeft = this.dragStartScroll - delta;
      }

      onPointerUp(event) {
        if (!this.pointerDown || event.pointerId !== this.activePointerId) return;
        const wasDragging = this.dragging;
        this.pointerDown = false;
        this.dragging = false;
        this.track.classList.remove('is-dragging');
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.activePointerId = null;
        this.draggedRecently = wasDragging;
        setTimeout(() => {
          this.draggedRecently = false;
        });
        this.queueUpdate();
      }

      preventDraggedLink(event) {
        if (this.draggedRecently && event.target.closest('a')) {
          event.preventDefault();
          event.stopPropagation();
        }
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }
    }
  );
}
